import React, {useState} from 'react';
import Icon from '../Icons/Icon';
import { useSearch } from './index';
import { useSavedSearches, useSuggestName } from '../../Data/SavedSearches';
import Form, { Actions, RequiredValidator, TextField, ULID } from '../Form';
import Modal from '../Modal';
import { classNames } from '../../Services/helpers';
import { useSession } from '../Session';
import { useDialogues } from '../Dialogues';

const SavedSearches = (props) => {
    const {hasFeature} = useSession();

    if (!props.searchCategory) {
        return null;
    }

    if (hasFeature('SAVED_SEARCHES') === false) {
        return null;
    }

    return <Inner {...props} />
}

const Inner = ({searchCategory}) => {
    const {filteredData, setSearchData, hasSearchConditions} = useSearch();

    const {searches, saveSearch, deleteSearch, refresh} = useSavedSearches(searchCategory, true);
    const [isSaving, setIsSaving] = useState(false);
    const {confirm} = useDialogues();

    if (!searches) {
        return null;
    }

    const currentSearchSaved = hasSearchConditions && searches.some(search => compareSearches(search.data, filteredData));

    return <div className="m-2">
        <div className="flex flex-row flex-wrap w-full gap-x-4 gap-y-1 items-center justify-center">
            {searches.map((search, i) => {
                const active = compareSearches(search.data, filteredData);

                return <Pill key={search.id}
                    {...{
                        label: search.label,
                        variant: active ? 'active' : null,
                        results: search.results ?? null,
                        onClick: () => setSearchData(search.data),
                        deleteClick: active ? () => confirm(
                            {
                                theme: 'danger',
                                title: 'Delete search',
                                message: `Are you sure you want to delete the search "${search.label}"?`,
                                confirmLabel: 'Delete',
                            },
                            () => deleteSearch(search.id),
                        ) : null,
                    }}
                />
            })}
            {(hasSearchConditions && !currentSearchSaved) && <Pill {...{
                label: 'Save',
                variant: 'add',
                save: true,
                results: <Icon icon="Plus" theme="solid" size="0.5x" />,
                onClick: () => setIsSaving(true)
            }} />}
        </div>

        {isSaving && <SaveCurrentSearch
            onChange={refresh}
            {...{searchCategory, isSaving, setIsSaving, saveSearch}}
        />}
    </div>
}

const Pill = ({label, results, variant, onClick, deleteClick}) => {
    const hasResults = results !== null && results !== undefined;

    return <div className={classNames(
            'rounded-full text-sm cursor-pointer border text-nowrap overflow-hidden',
            'flex flex-row items-center',
            hasResults ? 'p-0 pl-2' : 'px-2',
            variant === 'active' &&  'border-blue-500 bg-brand text-white fill-red-600',
            variant === 'add' && 'border-brand bg-slate-50 text-brand fill-brand',
            !variant && 'border-slate-200 bg-slate-50 text-slate-800 fill-slate-800',
        )}
        onClick={() => onClick && onClick()}
    >
        {label}
        &nbsp;
        {(hasResults || deleteClick) && <span className={classNames(
                'ml-1 px-2 rounded-full',
                variant === 'active' && 'bg-slate-200',
                variant === 'add' && 'bg-blue-100',
                !variant && 'bg-slate-200',
            )}
            >
            {deleteClick ? <Icon icon="Trash" size="0.5x" onClick={deleteClick} /> : results}
        </span>}
    </div>
}

const compareSearches = (search1, search2) => {
    return JSON.stringify(normaliseSearch(search1)) === JSON.stringify(normaliseSearch(search2))
}

const normaliseSearch = (search) => {
    if (!search) {
        return {};
    }

    return Object.keys(search).sort((a, b) => a === b ? 0 : (a < b ? 1 : -1)).reduce((acc, key) => {
        if (search[key] !== null && search[key] !== undefined) {
            acc[key] = search[key];
        }
        return acc;
    }, {});
}

const SaveCurrentSearch = ({isSaving, setIsSaving, searchCategory, saveSearch, onChange}) => {
    const {filteredData} = useSearch();

    const suggestion = useSuggestName(searchCategory, filteredData, isSaving);

    const handleSaveSearch = async (data) => {
        return saveSearch({
            label: data.label ?? suggestion,
            ...data
        });
    }

    return <Modal isOpen={isSaving} setIsOpen={setIsSaving} title="Save Search" dismissible>
        <Form data={{
                data: filteredData,
                category: searchCategory
            }}
            onSubmit={handleSaveSearch}
            afterSubmit={onChange}
            closeModalOnSubmit
        >
            <ULID name="id" />
            <TextField label="Save current search"
                placeholder={suggestion}
                validators={suggestion ? [] : [RequiredValidator]}
                name="label"
                className="mt-0"
            />
            <Actions submit={{label: 'Save'}} cancel />
        </Form>
    </Modal>
}

export default SavedSearches;